import * as React from 'react';
import { useState, useCallback } from 'react';
import {
    Alert,
    Box,
    Button,
    Divider,
    List,
    ListItem,
    ListItemText,
    Paper,
    Stack,
    Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import FileCopyIcon from '@mui/icons-material/FileCopyOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import { apiBaseUrl, httpClient, adminRequest } from '../config/api';

const formatDateTime = (value) => {
    if (!value) {
        return 'à l’instant';
    }
    try {
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) {
            return value;
        }
        return new Intl.DateTimeFormat('fr-FR', {
            dateStyle: 'medium',
            timeStyle: 'short',
        }).format(date);
    } catch (e) {
        return value;
    }
};

export default function SitemapToolsPage() {
    const [loading, setLoading] = useState(false);
    const [lastRun, setLastRun] = useState(null);
    const [error, setError] = useState('');
    const [publishLoading, setPublishLoading] = useState(false);
    const [publishInfo, setPublishInfo] = useState(null);
    const [publishError, setPublishError] = useState('');

    const regenerate = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const { json } = await httpClient(`${apiBaseUrl}/api/sitemap`, {
                method: 'GET',
            });
            const entries = Array.isArray(json?.entries) ? json.entries : [];
            setLastRun({
                generatedAt: json?.generatedAt || new Date().toISOString(),
                count: entries.length,
            });
        } catch (e) {
            console.error(e);
            setError(
                "Impossible d'appeler l'API sitemap. Réessaie plus tard ou vérifie que le backend est démarré."
            );
        } finally {
            setLoading(false);
        }
    }, []);

    const publishToPublicFile = useCallback(async () => {
        setPublishLoading(true);
        setPublishError('');
        setPublishInfo(null);
        try {
            const result = await adminRequest('/sitemap/publish', { method: 'POST' });
            setPublishInfo({
                count: result?.count ?? 0,
                path: result?.path ?? 'public/sitemap.xml',
                fileCount: result?.fileCount ?? 1,
                files: Array.isArray(result?.files) ? result.files : [],
                at: new Date().toISOString(),
            });
        } catch (e) {
            console.error(e);
            setPublishError(
                e?.message ||
                    "Impossible d'écrire le sitemap. Vérifie la configuration côté backend et réessaie."
            );
        } finally {
            setPublishLoading(false);
        }
    }, []);

    return (
        <Box sx={{ p: 2, maxWidth: 840 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1.5 }}>
                Génération du sitemap
            </Typography>
            <Typography variant="body2" color="text.secondary">
                Ce panneau permet de relancer manuellement la construction du fichier <code>sitemap.xml</code>{' '}
                à partir des données temps réel exposées par l’API Witwik.
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 3 }}>
                <Button
                    variant="contained"
                    startIcon={<RefreshIcon />}
                    onClick={regenerate}
                    disabled={loading}
                >
                    {loading ? 'Relance en cours…' : 'Relancer la génération'}
                </Button>
                <Button
                    variant="outlined"
                    startIcon={<OpenInNewIcon />}
                    component="a"
                    href={`${apiBaseUrl}/sitemap.xml`}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    Ouvrir l’index sitemap.xml
                </Button>
                <Button
                    variant="outlined"
                    startIcon={<FileCopyIcon />}
                    onClick={publishToPublicFile}
                    disabled={publishLoading}
                >
                    {publishLoading ? 'Copie en cours…' : 'Publier les fichiers sitemap'}
                </Button>
            </Stack>

            {error && (
                <Alert
                    severity="error"
                    icon={<ErrorOutlineIcon fontSize="inherit" />}
                    sx={{ mt: 3 }}
                >
                    {error}
                </Alert>
            )}

            {publishError && (
                <Alert
                    severity="error"
                    icon={<ErrorOutlineIcon fontSize="inherit" />}
                    sx={{ mt: 2 }}
                >
                    {publishError}
                </Alert>
            )}

            {publishInfo && (
                <Alert
                    severity="success"
                    icon={<CheckCircleOutlineIcon fontSize="inherit" />}
                    sx={{ mt: 2 }}
                >
                    Sitemap index copié dans <code>{publishInfo.path}</code> — {publishInfo.count} URL
                    réparties dans {publishInfo.fileCount} fichier(s) — {formatDateTime(publishInfo.at)}.
                    {publishInfo.files?.length > 0 && (
                        <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                            {publishInfo.files.slice(0, 12).map((file) => (
                                <li key={file}>
                                    <code>{file}</code>
                                </li>
                            ))}
                            {publishInfo.files.length > 12 && (
                                <li>… {publishInfo.files.length - 12} fichier(s) supplémentaire(s)</li>
                            )}
                        </Box>
                    )}
                </Alert>
            )}

            {lastRun && (
                <Alert
                    severity="success"
                    icon={<CheckCircleOutlineIcon fontSize="inherit" />}
                    sx={{ mt: 3 }}
                >
                    Sitemap recalculé le {formatDateTime(lastRun.generatedAt)} —{' '}
                    {lastRun.count} URL(s) listées.
                </Alert>
            )}

            <Paper variant="outlined" sx={{ mt: 4 }}>
                <Box sx={{ p: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Mode opératoire recommandé
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Après déclenchement, le backend écrit un sitemap index et des fichiers
                        typés par famille puis par lettre.
                    </Typography>
                </Box>
                <Divider />
                <List dense>
                    <ListItem>
                        <ListItemText primary="1. Relancer la génération via le bouton ci-dessus." />
                    </ListItem>
                    <ListItem>
                        <ListItemText primary="2. Télécharger le flux JSON ou l’ouvrir dans un nouvel onglet." />
                    </ListItem>
                    <ListItem>
                        <ListItemText primary="3. Soumettre sitemap.xml à Google Search Console : il référence les fichiers rdf/by-subject/by-oobject par lettre." />
                    </ListItem>
                </List>
            </Paper>
        </Box>
    );
}
